/**
 * LotKpiBar — Inline KPI strip for lot cards in Engineering > Structure.
 * Shows: files count, retained count, generations (shoebox/fps split), last action.
 * Plus a colored status pill (À démarrer / En cours / Prêt à générer / Généré / Approuvé client).
 */
import { FileText, Star, Sparkles, Clock } from 'lucide-react';

// Returns one of: 'todo' | 'in_progress' | 'ready' | 'generated' | 'approved'
export function computeLotStatus(lot, filesForLot, generationsForLot) {
  const isApproved = ['validation_client', 'livraison_finale'].includes(lot?.workflow_status);
  if (isApproved) return 'approved';
  if ((generationsForLot?.length || 0) > 0) return 'generated';
  if (lot?.selected_plan_file_id) return 'ready';
  if ((filesForLot?.length || 0) > 0) return 'in_progress';
  return 'todo';
}

const STATUS_META = {
  todo: { label: 'À démarrer', color: '#64748B', bg: 'rgba(100,116,139,0.15)', border: 'rgba(100,116,139,0.35)' },
  in_progress: { label: 'En cours', color: '#F97316', bg: 'rgba(249,115,22,0.15)', border: 'rgba(249,115,22,0.35)' },
  ready: { label: 'Prêt à générer', color: '#D9A05B', bg: 'rgba(217,160,91,0.15)', border: 'rgba(217,160,91,0.45)' },
  generated: { label: 'Généré', color: '#22C55E', bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.35)' },
  approved: { label: 'Approuvé client', color: '#10B981', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.45)' },
};

function timeAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'à l\'instant';
  if (s < 3600) return `il y a ${Math.floor(s / 60)}min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)}h`;
  if (s < 604800) return `il y a ${Math.floor(s / 86400)}j`;
  if (s < 2592000) return `il y a ${Math.floor(s / 604800)}sem`;
  return `il y a ${Math.floor(s / 2592000)}mo`;
}

export function LotStatusPill({ status, testid }) {
  const meta = STATUS_META[status] || STATUS_META.todo;
  return (
    <span
      className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded font-semibold inline-flex items-center"
      style={{
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.border}`,
      }}
      data-testid={testid}
    >
      {meta.label}
    </span>
  );
}

export default function LotKpiBar({ lot, files = [], generations = [], onOpenFiles }) {
  // V14.0 — When this lot is a Similaire, its KPIs reflect those of its master
  // Unique lot. This is the same id-substitution applied by the backend on
  // /lots/{id}/files and /lots/{id}/generations, mirrored client-side here so
  // the rolled-up KPI strip on the parent list matches what the user will see
  // when they open the lot detail.
  const effectiveLotId = lot.master_lot_id || lot.lot_id;
  const lotFiles = files.filter((f) => f.lot_id === effectiveLotId);
  const retainedFiles = lotFiles.filter((f) => f.is_retained);
  const lotGens = generations.filter((g) => g.lot_id === effectiveLotId);
  const shoeboxCount = lotGens.filter((g) => (g.view_type || 'shoebox') === 'shoebox').length;
  const fpsCount = lotGens.filter((g) => g.view_type === 'fps').length;
  // Latest 3 thumbnails (newest first)
  const thumbs = [...lotGens]
    .sort((a, b) => (b.completed_at || b.created_at || '').localeCompare(a.completed_at || a.created_at || ''))
    .slice(0, 3);

  // Compute last action
  const dates = [
    ...lotFiles.map((f) => f.created_at),
    ...lotGens.map((g) => g.completed_at || g.created_at),
    ...((lot.workflow_notes || []).map((n) => n.timestamp || n.created_at)),
    lot.updated_at,
  ].filter(Boolean);
  const lastAction = dates.length ? dates.sort().slice(-1)[0] : null;

  const Tile = ({ icon: Icon, label, value, color, onClick, testid }) => (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] tabular-nums transition-colors"
      style={{
        background: 'rgba(255,255,255,0.04)',
        color: color || 'rgba(255,255,255,0.65)',
        border: '1px solid rgba(255,255,255,0.07)',
        cursor: onClick ? 'pointer' : 'default',
      }}
      title={label}
    >
      <Icon className="w-3 h-3" />
      <span className="font-semibold">{value}</span>
      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{label}</span>
    </button>
  );

  return (
    <div className="space-y-1.5 mt-1.5" data-testid={`lot-kpis-${lot.lot_id}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Tile
          icon={FileText}
          label="fichiers"
          value={lotFiles.length}
          color={lotFiles.length > 0 ? '#94a3b8' : 'rgba(255,255,255,0.35)'}
          onClick={onOpenFiles}
          testid={`lot-kpi-files-${lot.lot_id}`}
        />
        <Tile
          icon={Star}
          label="retenus"
          value={retainedFiles.length}
          color={retainedFiles.length > 0 ? '#D9A05B' : 'rgba(255,255,255,0.35)'}
          testid={`lot-kpi-retained-${lot.lot_id}`}
        />
        <Tile
          icon={Sparkles}
          label={`gen${lotGens.length > 1 ? 's' : ''}`}
          value={
            lotGens.length === 0
              ? '0'
              : `${shoeboxCount}S · ${fpsCount}F`
          }
          color={lotGens.length > 0 ? '#22C55E' : 'rgba(255,255,255,0.35)'}
          testid={`lot-kpi-gens-${lot.lot_id}`}
        />
        {lastAction && (
          <span
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            title={new Date(lastAction).toLocaleString('fr-FR')}
            data-testid={`lot-kpi-last-${lot.lot_id}`}
          >
            <Clock className="w-3 h-3" />
            {timeAgo(lastAction)}
          </span>
        )}
      </div>

      {/* Mini thumbnails strip — show up to 3 most recent generations */}
      {thumbs.length > 0 && (
        <div
          className="flex items-center gap-1.5"
          data-testid={`lot-thumbs-${lot.lot_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {thumbs.map((g) => {
            const isFps = g.view_type === 'fps';
            return (
              <a
                key={g.job_id}
                href={`${process.env.REACT_APP_BACKEND_URL}${g.image_url || `/api/lots/${g.lot_id}/generations/${g.job_id}/image`}`}
                target="_blank"
                rel="noopener noreferrer"
                className="relative w-12 h-12 rounded overflow-hidden block group/thumb"
                style={{
                  border: `1px solid ${isFps ? 'rgba(20,184,166,0.45)' : 'rgba(217,160,91,0.45)'}`,
                  background: '#0a0a0d',
                }}
                title={`${isFps ? 'FPS' : 'Shoebox'} · ${new Date(g.completed_at || g.created_at).toLocaleDateString('fr-FR')}`}
                data-testid={`lot-thumb-${g.job_id}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${g.image_url || `/api/lots/${g.lot_id}/generations/${g.job_id}/image`}`}
                  alt={g.job_id}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover/thumb:scale-110 transition-transform duration-300"
                />
                <span
                  className="absolute bottom-0 left-0 right-0 text-[7px] text-center py-0.5 font-bold uppercase tracking-wider"
                  style={{
                    background: isFps ? 'rgba(20,184,166,0.85)' : 'rgba(217,160,91,0.85)',
                    color: '#000',
                  }}
                >
                  {isFps ? `FPS` : 'SB'}
                </span>
              </a>
            );
          })}
          {lotGens.length > 3 && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{
                background: 'rgba(255,255,255,0.04)',
                color: 'rgba(255,255,255,0.55)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              +{lotGens.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FloorWorkflowBar — Progress bar for a floor: 4 steps
 * (uploadés / plan retenu / générations / approuvé) showing lot count per step.
 */
export function FloorWorkflowBar({ floor, lotsInFloor, files, generations }) {
  const total = lotsInFloor.length;
  if (total === 0) return null;
  const stats = lotsInFloor.reduce(
    (acc, l) => {
      // V14.0 — Use master_lot_id when present so Similaire lots count as
      // "complete" once their master has files/generations.
      const effId = l.master_lot_id || l.lot_id;
      const f = files.filter((x) => x.lot_id === effId);
      const g = generations.filter((x) => x.lot_id === effId);
      const status = computeLotStatus(l, f, g);
      if (f.length > 0) acc.uploaded += 1;
      if (l.selected_plan_file_id) acc.selected += 1;
      if (g.length > 0) acc.generated += 1;
      if (status === 'approved') acc.approved += 1;
      return acc;
    },
    { uploaded: 0, selected: 0, generated: 0, approved: 0 }
  );

  const steps = [
    { label: 'Plans uploadés', count: stats.uploaded, color: '#64748B' },
    { label: 'Plan retenu', count: stats.selected, color: '#D9A05B' },
    { label: 'Générations', count: stats.generated, color: '#22C55E' },
    { label: 'Approuvé', count: stats.approved, color: '#10B981' },
  ];

  return (
    <div className="flex items-center gap-2 mb-2 text-[10px] flex-wrap" data-testid={`floor-workflow-${floor.floor_id}`}>
      {steps.map((s, i) => {
        const pct = total ? (s.count / total) * 100 : 0;
        return (
          <div key={s.label} className="flex items-center gap-1.5">
            <div
              className="relative w-20 h-1.5 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.08)' }}
              title={`${s.count}/${total} lots — ${s.label}`}
            >
              <div
                className="absolute inset-y-0 left-0 transition-all"
                style={{ width: `${pct}%`, background: s.color, boxShadow: `0 0 6px ${s.color}66` }}
              />
            </div>
            <span style={{ color: 'rgba(255,255,255,0.55)' }}>
              {s.count}<span style={{ color: 'rgba(255,255,255,0.3)' }}>/{total}</span>
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>{s.label}</span>
            {i < steps.length - 1 && <span style={{ color: 'rgba(255,255,255,0.15)' }}>→</span>}
          </div>
        );
      })}
    </div>
  );
}
